import {
  accessRoleEnum,
  relationshipTypeEnum,
  roleEnum,
  users,
} from '@markku/db'
import { assertNotNil } from '@markku/utils'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'
import { attributionPayloadSchema } from './acquisition'
import { emailSchema, phoneNumberSchema } from './common'

export type Role = (typeof roleEnum.enumValues)[number]

/**
 * Recipient used when a notification should reach "a manager" but no team
 * leader can be resolved (employee has no team, team has no leader, or the
 * reporter is themselves the leader). Plain email rather than a userId so it
 * works even if the recipient has no user record in our DB.
 */
export const FALLBACK_MANAGER_EMAIL = 'jani.issakainen@markku.fi'

// Role hierarchy: each role inherits permissions from roles below it
// Record ensures all roles are defined (TypeScript enforces completeness)
const ROLE_HIERARCHY: Record<Role, readonly Role[]> = {
  admin: [
    'admin',
    'service_manager',
    'officer',
    'employee',
    'management',
    'user',
  ],
  officer: ['officer', 'user'],
  service_manager: ['service_manager', 'officer', 'employee', 'user'],
  employee: ['employee', 'user'],
  // Grants the johtoryhmä report and its targets. Carries officer because the
  // ERP login gate requires it, so a board member is an officer in the ERP too.
  management: ['management', 'officer', 'user'],
  user: ['user'],
} as const

// App identifiers for role scoping
export type AppId = 'erp' | 'employee' | 'resident' | 'web'

// Maximum roles each app can use — roles outside this set are stripped
const APP_MAX_ROLES: Record<AppId, readonly Role[]> = {
  erp: [
    'admin',
    'service_manager',
    'officer',
    'employee',
    'management',
    'user',
  ],
  employee: ['employee', 'user'],
  resident: ['user'],
  web: ['user'],
} as const

/**
 * Expands roles array based on hierarchy
 * @example expandRoles(['admin']) // returns ['admin', 'officer', 'user']
 */
export function expandRoles(roles: Role[]): Role[] {
  return [
    ...new Set(
      roles.flatMap((role) => {
        return assertNotNil(
          ROLE_HIERARCHY[role],
          `Role hierarchy ${role} not found`,
        )
      }),
    ),
  ]
}

// Create Zod schemas from Drizzle table schema. internal* fields are stripped
// for non-staff roles, so make them optional on the response schema.
export const selectUserSchema = createSelectSchema(users, {
  // Roles isn't automatically inferred as array from the DB definition, so we need to manually define it
  roles: z.enum(roleEnum.enumValues).array(),
  internalFirstTouchAcquisition: z.unknown().optional(),
})

export const insertUserSchema = createInsertSchema(users, {
  phoneNumber: phoneNumberSchema,
  // '' means "clear the email" (phone-only contact), so it maps to null
  // instead of failing emailSchema's format check.
  email: z.preprocess(
    (val) => (val === '' ? null : val),
    emailSchema.nullable().optional(),
  ),
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  emailVerifiedAt: true,
})

// Public user schema (without sensitive data)
export const publicUserSchema = selectUserSchema.omit({
  emailVerifiedAt: true,
})

export const updateUserSchema = insertUserSchema.partial().extend({
  id: z.number().int().positive(),
})

// User with customer memberships schema (for user.me endpoint)
export const selectUserWithMembershipsSchema = selectUserSchema.extend({
  customerMemberships: z
    .array(
      z.object({
        id: z.number(),
        customerId: z.number(),
        accessRole: z.enum(accessRoleEnum.enumValues).nullable(),
        relationshipType: z.enum(relationshipTypeEnum.enumValues).nullable(),
        customer: z.object({
          id: z.number(),
          name: z.string(),
        }),
      }),
    )
    .optional(),
})

// Type exports
export type User = z.infer<typeof selectUserSchema>
export type UserWithMemberships = z.infer<
  typeof selectUserWithMembershipsSchema
>
export type PublicUser = z.infer<typeof publicUserSchema>
export type CreateUser = z.infer<typeof insertUserSchema>
export type UpdateUser = z.infer<typeof updateUserSchema>

// Utility functions for role management
export function hasRole(userRoles: Role[], requiredRole: Role): boolean {
  return expandRoles(userRoles).includes(requiredRole)
}

export function hasAnyRole(userRoles: Role[], requiredRoles: Role[]): boolean {
  const expanded = expandRoles(userRoles)
  return requiredRoles.some((role) => expanded.includes(role))
}

// Privileged (staff) roles — anyone who is not a plain contact. Used to keep
// officers from editing colleagues' accounts (e.g. changing an unverified
// staff email is a soft account-takeover vector).
export const STAFF_ROLES: Role[] = [
  'admin',
  'service_manager',
  'officer',
  'employee',
]

// Roles that carry officer: who can open the inbox and the office views.
export const OFFICER_ROLES = (Object.keys(ROLE_HIERARCHY) as Role[]).filter(
  (role) => ROLE_HIERARCHY[role].includes('officer'),
)

// The staff directory as non-officers may see it: enough to pick a colleague.
export const staffUserSchema = z.object({
  id: z.number(),
  firstName: z.string(),
  lastName: z.string(),
  // Null for office staff without an employee profile.
  teamId: z.number().nullable(),
  // False for field workers, who can't open what the inbox would hand them.
  isOfficer: z.boolean(),
})
export type StaffUser = z.infer<typeof staffUserSchema>

// True if the user holds any staff role. `hasAnyRole` expands the hierarchy,
// so ['user'] → false and ['officer'] → true.
export function isStaff(roles: Role[]): boolean {
  return hasAnyRole(roles, STAFF_ROLES)
}

// Canonical email normalization for storage and comparison. Empty/nullish → undefined.
export function normalizeEmail(
  email: string | null | undefined,
): string | undefined {
  return email ? email.toLowerCase().trim() : undefined
}

export function scopeRolesToApp(
  roles: Role[],
  appId: string | null | undefined,
): Role[] {
  if (!appId || !(appId in APP_MAX_ROLES)) return roles
  const allowed = APP_MAX_ROLES[appId as AppId]
  return roles.filter((role) => allowed.includes(role))
}

// OTP-related schemas (TODO remove / refactor these)
export const requestOtpSchema = z.object({
  email: emailSchema,
  turnstileToken: z.string().min(1).optional(),
})

export const verifyOtpSchema = z.object({
  email: emailSchema,
  token: z.string().min(6).max(6), // 6-digit OTP
  app: z.enum(['erp', 'employee', 'resident']),
  marketingConsent: z.boolean().optional(),
  /** Full first/last-touch payload from the client's mk_attr cookie. */
  attribution: attributionPayloadSchema.optional(),
})
export type RequestOtp = z.infer<typeof requestOtpSchema>
export type VerifyOtp = z.infer<typeof verifyOtpSchema>

// Google OAuth schemas
export const googleCallbackSchema = z.object({
  code: z.string().min(1),
  app: z.enum(['resident']),
  /** Full first/last-touch payload from the client's mk_attr cookie. */
  attribution: attributionPayloadSchema.optional(),
})

export const googleLoginResponseSchema = z.object({
  url: z.string().url(),
})

export type GoogleCallback = z.infer<typeof googleCallbackSchema>
export type GoogleLoginResponse = z.infer<typeof googleLoginResponseSchema>
